import type { TournamentRoundsApi } from '@/v2/api/metadata/tournament-rounds'
import type { BackofficeContext } from '@/v2/framework/context/types'
import type { ColumnEntry } from '@/v2/framework/column/types'
import { ColumnIconType, FieldType } from '@/v2/framework/column/types'
import type {
  ContentTab,
  DialogConfig,
  DialogContentNode,
  DialogTabSaveContext,
  GridTab,
  GridTabSaveContext,
  TranslationsTab,
} from '@/v2/framework/dialog/types'
import type { GridViewBlock } from '@/v2/framework/view/types'
import { readApiData } from '@/v2/api/response'
import { formatTranslationsForUpdate } from '@/services/helpers'
import { t } from '@/services/i18n'
import {
  addFeedMappingRow,
  buildFeedMappingGrid,
  createFeedMappingSubmitGate,
  fetchFeedTypeMappingsList,
} from '@/v2/kits/feed-mapping'
import {
  buildAddSupplementalNamesPayload,
  buildSupplementalNamesPatch,
  syncSupplementalNamesBaseline,
} from '@/v2/kits/supplemental-names'
import { createInheritableTabSave } from '@/v2/kits/tenant-inheritance'
import { notify, notifyApiError, notifyApiSuccess } from '@/v2/notifications'
import { NXGDSStatusVariant } from '@nsftx/seven-nxg-design-system-backoffice'
import {
  displayIdRowFields,
  tournamentDefaultEventSelectionOptions,
  tournamentGamesStatusOptions,
  tournamentStatusOptions,
} from '../../../shared'
import { buildTournamentTranslationFields } from '../translation-fields'

function roundId(values: Record<string, unknown>): string | number {
  return values.id as string | number
}

function notifySuccess(): void {
  notifyApiSuccess('Successfully updated.')
}

const roundColumns: ColumnEntry[] = [
  { key: 'id', defaultVisibility: true, title: 'table_columns.id', fieldType: FieldType.TextCell },
  {
    key: 'status',
    defaultVisibility: true,
    title: 'table_columns.status',
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => {
      const status = tournamentStatusOptions.find(
        (option) => option.value === (row as { status?: number }).status,
      )
      return t(status?.title ?? '')
    },
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => {
        const status = tournamentStatusOptions.find(
          (option) => option.value === (row as { status?: number }).status,
        )
        return { code: status?.icon?.code ?? '', color: status?.icon?.color ?? '' }
      },
    },
  },
  {
    key: 'name',
    defaultVisibility: true,
    title: 'table_columns.name',
    fieldType: FieldType.InputCell,
  },
  {
    key: 'info',
    defaultVisibility: false,
    title: 'table_columns.info',
    fieldType: FieldType.TextCell,
  },
  {
    key: 'gridChrome',
    defaultVisibility: true,
    configurableVisibility: false,
    title: '',
    fieldType: FieldType.ActionsCell,
    sticky: true,
    rowActions: [
      { icon: 'edit-ng', label: 'Edit', onClick: (ctx) => ctx.openDialog('edit') },
      { icon: 'trash-ng', label: 'Delete', onClick: (ctx) => ctx.openDialog('delete') },
    ],
  },
]

function gameFields(): DialogContentNode[] {
  return [
    {
      type: 'select-field',
      name: 'status',
      label: 'Status',
      required: true,
      options: tournamentGamesStatusOptions,
      inheritance: { computedKey: 'computedStatus', parentKey: 'parentStatus' },
    },
    {
      type: 'text-field',
      name: 'position',
      label: 'Position',
      inheritance: { computedKey: 'computedPosition', parentKey: 'parentPosition' },
    },
    {
      type: 'structured-array-field',
      name: 'displayIds',
      label: 'Display IDs',
      minItems: 0,
      maxItems: 50,
      inheritance: { computedKey: 'computedDisplayIds', parentKey: 'parentDisplayIds' },
      children: displayIdRowFields,
    },
    {
      type: 'select-field',
      name: 'defaultEventSelection',
      label: 'Default Event Selection',
      required: true,
      options: tournamentDefaultEventSelectionOptions,
      inheritance: {
        computedKey: 'defaultEventSelection',
        parentKey: 'parentDefaultEventSelection',
      },
    },
  ]
}

function gameGroup(name: 'prematchGame' | 'liveGame'): ContentTab['children'] {
  return [
    {
      type: 'group',
      name,
      label: '',
      children: gameFields(),
    },
  ]
}

function createRoundDialogs(options: {
  ctx: BackofficeContext
  roundsApi: TournamentRoundsApi
  getTournamentId: () => unknown
}): GridViewBlock['dialogs'] {
  const { ctx, roundsApi, getTournamentId } = options

  const mainChildren: ContentTab['children'] = [
    {
      type: 'select-field',
      name: 'status',
      label: 'Status',
      required: true,
      options: tournamentStatusOptions,
      inheritance: { computedKey: 'computedStatus', parentKey: 'parentStatus' },
    },
    {
      type: 'text-field',
      name: 'name',
      label: 'Name',
      placeholder: 'Enter name',
      inheritance: { computedKey: 'computedName', parentKey: 'parentName' },
      validate: (value, values) => {
        if (values.isTenantSourceOfEntity && !String(value ?? '').trim()) return 'Required'
        return undefined
      },
    },
    {
      type: 'text-field',
      name: 'info',
      label: 'Info',
      placeholder: 'Enter info',
      inheritance: { computedKey: 'computedInfo', parentKey: 'parentInfo' },
    },
  ]

  const mainTab: ContentTab = {
    id: 'main',
    label: 'Main',
    save: createInheritableTabSave({
      children: mainChildren,
      buildExtraPatch: (saveCtx) => buildSupplementalNamesPatch(saveCtx.values, saveCtx.baseline),
      afterPersist: syncSupplementalNamesBaseline,
      persist: async (patch, saveCtx: DialogTabSaveContext) => {
        await roundsApi.update(roundId(saveCtx.values), patch)
        notifySuccess()
      },
    }),
    children: [
      ...mainChildren,
      {
        type: 'component',
        component: () =>
          import('@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue'),
        props: { mode: 'edit', entity: 'TOURNAMENT' },
      },
    ],
  }

  const prematchChildren = gameGroup('prematchGame')
  const liveChildren = gameGroup('liveGame')

  const translationsTab: TranslationsTab = {
    kind: 'translations',
    id: 'translations',
    label: 'Translations',
    persistence: 'isolated',
    parentSubmitWithUnsavedTranslations: 'block',
    fields: buildTournamentTranslationFields,
    api: {
      get: async ({ values }) => {
        const translations = readApiData(await roundsApi.getTranslations(roundId(values)))
        const list = Array.isArray(translations) ? translations : []
        const translationsPerLanguage = (list as Array<{ languageId: string | number }>).reduce(
          (acc, translation) => {
            acc.set(String(translation.languageId), translation)
            return acc
          },
          new Map(),
        )
        return { languages: ctx.read.languages(), translationsPerLanguage }
      },
      save: async (model, saveCtx) => {
        await roundsApi.update(roundId(saveCtx.values), {
          translations: formatTranslationsForUpdate(
            model as Parameters<typeof formatTranslationsForUpdate>[0],
          ),
        })
      },
    },
  }

  const feedMappingTab: GridTab = {
    kind: 'grid',
    id: 'feed-mapping',
    label: 'Feed mapping',
    createGrid: (getValues) =>
      buildFeedMappingGrid(
        () => roundId(getValues()),
        roundsApi.getFeedTypeMappings.bind(roundsApi),
      ),
    createSubmitGate: createFeedMappingSubmitGate,
    addRowLabel: 'common.buttons.add_new',
    onAddRow: addFeedMappingRow,
    save: async (saveCtx: GridTabSaveContext) => {
      const gate = saveCtx.gate
      if (!gate?.hasSuccessfulLoad() || !gate.isDirty()) return
      if (!gate.validateForDialogSubmit()) throw new Error('Please fill the required fields')
      const fragment = gate.buildMergeFragment()
      if (!fragment) return
      const entityId = roundId(saveCtx.values)
      await roundsApi.update(entityId, fragment)
      if (!saveCtx.rows) throw new Error('Feed mapping grid is not available')
      saveCtx.rows.value = await fetchFeedTypeMappingsList(
        roundsApi.getFeedTypeMappings,
        entityId,
        {
          isReadAfterWrite: true,
        },
      )
      gate.markClean?.()
      notifySuccess()
    },
  }

  const edit: DialogConfig = {
    id: 'edit-tournament-round',
    title: 'Edit tournament round',
    layout: { type: 'popover' },
    api: {
      initialValues: async (row) => {
        const response = await roundsApi.getSingle(row.id)
        const data = readApiData(response)
        return (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as Record<
          string,
          unknown
        >
      },
      submit: async () => {},
    },
    structure: {
      type: 'tabbed',
      savePerTab: true,
      tabs: [
        mainTab,
        {
          id: 'prematch',
          label: 'Prematch',
          save: createInheritableTabSave({
            children: prematchChildren,
            persist: async (patch, saveCtx) => {
              await roundsApi.update(roundId(saveCtx.values), patch)
              notifySuccess()
            },
          }),
          children: prematchChildren,
        },
        {
          id: 'live',
          label: 'Live',
          save: createInheritableTabSave({
            children: liveChildren,
            persist: async (patch, saveCtx) => {
              await roundsApi.update(roundId(saveCtx.values), patch)
              notifySuccess()
            },
          }),
          children: liveChildren,
        },
        translationsTab,
        feedMappingTab,
      ],
    },
  }

  const add: DialogConfig = {
    id: 'add-tournament-round',
    title: 'Add tournament round',
    trigger: { type: 'fab' },
    layout: { type: 'popover' },
    structure: {
      type: 'stepper',
      steps: [
        {
          id: 'main',
          children: [
            {
              type: 'group',
              name: 'main',
              label: 'Main',
              children: [
                {
                  type: 'select-field',
                  name: 'status',
                  label: 'Status',
                  required: true,
                  options: tournamentStatusOptions,
                  defaultValue: 1,
                },
                {
                  type: 'text-field',
                  name: 'name',
                  label: 'Name',
                  required: true,
                  placeholder: 'Enter a name',
                },
                {
                  type: 'text-field',
                  name: 'info',
                  label: 'Info',
                  placeholder: 'Enter info',
                },
                {
                  type: 'component',
                  component: () =>
                    import('@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue'),
                  props: { mode: 'add', entity: 'TOURNAMENT' },
                },
              ],
            },
            {
              type: 'group',
              name: 'prematchGame',
              label: 'Pre-match',
              children: gameFields(),
            },
            {
              type: 'group',
              name: 'liveGame',
              label: 'Live',
              children: gameFields(),
            },
          ],
        },
      ],
    },
    api: {
      submit: (data) => {
        const main = (data.main ?? {}) as Record<string, unknown>
        const prematchGame = (data.prematchGame ?? {}) as Record<string, unknown>
        const liveGame = (data.liveGame ?? {}) as Record<string, unknown>
        return roundsApi.create({
          tournamentId: getTournamentId(),
          status: main.status,
          name: main.name,
          info: main.info,
          supplementalNames: buildAddSupplementalNamesPayload(data, 'TOURNAMENT'),
          prematchGame: {
            status: prematchGame.status,
            position: prematchGame.position,
            displayIds: prematchGame.displayIds,
            defaultEventSelection: prematchGame.defaultEventSelection,
          },
          liveGame: {
            status: liveGame.status,
            position: liveGame.position,
            displayIds: liveGame.displayIds,
            defaultEventSelection: liveGame.defaultEventSelection,
          },
        })
      },
    },
  }

  const remove: DialogConfig = {
    id: 'delete-tournament-round',
    title: 'Delete tournament round',
    description: 'Are you sure you want to delete this tournament round?',
    layout: {
      type: 'nxgds-dialog',
      actions: [
        { intent: 'dismiss', label: 'Cancel', variant: 'secondary' },
        { intent: 'submit', label: 'Delete', variant: 'danger' },
      ],
    },
    structure: { type: 'flat', children: [] },
    api: {
      submit: async (_data, dialogCtx) => {
        try {
          await roundsApi.delete(dialogCtx.row?.id)
          notify({
            variant: NXGDSStatusVariant.Positive,
            text: `You have successfully deleted "${dialogCtx.row?.name}" tournament round.`,
          })
        } catch (error) {
          notifyApiError(error, `Unable to delete "${dialogCtx.row?.name}" tournament round.`)
        }
      },
    },
  }

  return { add, edit, delete: remove }
}

export function createRoundsTab(options: {
  ctx: BackofficeContext
  roundsApi: TournamentRoundsApi
}): GridTab {
  const { ctx, roundsApi } = options

  return {
    kind: 'grid',
    id: 'tournament-rounds',
    label: 'Tournament rounds',
    createGrid: (getValues): GridViewBlock => ({
      type: 'grid',
      columns: roundColumns,
      syncRouteQuery: false,
      dialogs: createRoundDialogs({
        ctx,
        roundsApi,
        getTournamentId: () => getValues().id,
      }),
      api: {
        get: (filters) =>
          roundsApi.grid.get({
            ...filters,
            tournamentId: getValues().id,
          }),
        update: roundsApi.grid.update,
        delete: roundsApi.grid.delete,
      },
    }),
  }
}
